import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useMediaQuery from "../../hooks/useMediaQuery";
import {
  FilterData,
  FilterGroupData,
  FilterSettings,
  IMapFilter,
} from "./map.interfaces";
import MapFilterDropdown from "./mapFilterDropdown";

import { CmsMapDataLayer } from "./hooks/useCmsMapData";
import { SearchData } from "./hooks/useMapData";
import MapFilterSearch from "./mapFilterSearch";
import { MapTypes } from "./mapConfig";
import { Route } from "../linerServiceAdminRoute";
import { PointLike } from "mapbox-gl";

export const FilterContext = React.createContext<FilterSettings>({
  activeFilterGroup: "",
  setActiveFilterGroup: () => {},
  activeFilters: [],
  setActiveFilters: () => [],
});

const groupBy = (x: any, f: any) =>
  x.reduce((a: any, b: any, i: any) => ((a[f(b, i, x)] ||= []).push(b), a), {});

const MapFilter = ({
  map,
  show,
  setFilterModalSize,
  layers,
  filterModalOpen,
  currentCmsMapData,
  customMapLayers,
  searchData,
}: IMapFilter) => {
  const ref = useRef(null);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(576);
  const [showFilterButton, setShowFilterButton] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeFilterGroup, setActiveFilterGroup] = useState<
    string | undefined
  >();
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  let resizeObserver: ResizeObserver | null;

  useEffect(() => {
    if (ref.current && !resizeObserver) {
      resizeObserver = new ResizeObserver((entries) => {
        updateFilterSize();
      });

      resizeObserver.observe(ref.current);

      return () => {
        resizeObserver?.unobserve(document.documentElement);
        resizeObserver = null;
      };
    }
  }, [ref.current]);

  // ------------------------------------
  // MAP CONTROLS SHOW EVENT CHANGES
  // ------------------------------------

  useEffect(() => {
    if (show) {
      updateFilterSize();
      setShowFilterButton(true);
      if (!isMobile) {
        setTimeout(() => {
          setShowFilterModal(true);
          updateFilterSize();
        }, 2000);
      }
    } else {
      setShowFilterModal(false);
    }
  }, [show]);

  useEffect(() => {
    if (currentCmsMapData) {
      const layerToShowFromStart = currentCmsMapData.mapLayers.filter(
        (cmsMapLayer) => cmsMapLayer.showFromStart && cmsMapLayer.showInFilter
      );
      if (currentCmsMapData?.mapType === MapTypes.LINER_SERVICE_MAP) {
        activeFilters.push("roro");
        activeFilters.push("container");
        activeFilters.push("bulk");
      }

      layerToShowFromStart.forEach((layer) => {
        activeFilters.push(layer.layerId);
        setActiveFilters([...activeFilters]);
      });
    }
  }, [currentCmsMapData]);

  // --------------------------------------------------------
  // ACTIVE FILTERS - handles filter selection
  // --------------------------------------------------------

  useEffect(() => {
    if (activeFilters && map && customMapLayers.length && currentCmsMapData) {
      const mapboxMap = map;
      const filterableLayers = customMapLayers.filter((customMapLayer) => {
        const cmsMapDataLayer = currentCmsMapData.mapLayers.find(
          (cmsMapLayer) => cmsMapLayer.layerId === customMapLayer.id
        );

        return cmsMapDataLayer?.showInFilter;
      });

      filterableLayers.forEach((layer) => {
        if (map.getLayer(layer.id)) {
          const hideFilter = activeFilters.some(
            (filterId) => layer.id === filterId
          );

          if (!hideFilter) {
            mapboxMap.setLayoutProperty(layer.id, "visibility", "none");
          }
        }
      });

      if (currentCmsMapData?.mapType === MapTypes.LINER_SERVICE_MAP) {
        mapboxMap.setFilter("ghab-linermap", null);

        const hasRoroFilter = activeFilters.some((filter) => filter === "roro");
        const hasContainerFilter = activeFilters.some(
          (filter) => filter === "container"
        );
        const hasBulkFilter = activeFilters.some(
          (filter) => filter === "bulk"
        );

        let filter: any[] = ["any"];

        if (hasContainerFilter) {
          filter.push(["==", "container", true]);
        }

        if (hasRoroFilter) {
          filter.push(["==", "roro", true]);
        }
        if (hasBulkFilter) {
          filter.push(["==", "bulk", true]);
        }
        if (filter.length > 1) {
          mapboxMap.setFilter("ghab-linermap", filter);
        }
      }
      activeFilters.forEach((filter) => {
        if (map.getLayer(filter)) {
          mapboxMap.setLayoutProperty(filter, "visibility", "visible");
        }
      });
    }
  }, [JSON.stringify(activeFilters), activeFilterGroup, map]);

  // ------------------------------------------------------------------------
  // GROUP FILTER DATA - Set data for the filter based on map-box-layers
  // ------------------------------------------------------------------------

  const groupFilterData: FilterGroupData[] = useMemo(() => {
    const filterLayers = layers.filter((layer) => layer.showInFilter);
    const groupedLayers: CmsMapDataLayer[][] = Object.values(
      groupBy(
        filterLayers,
        (layer: CmsMapDataLayer) => layer.groupName || layer.layerId
      )
    );

    const groupData = groupedLayers.map((layerGroup, index) => {
      const layersInGroup: FilterData[] = layerGroup.map((layer) => {
        return {
          id: layer.layerId,
          title: layer.layerName || layer.layerId,
          selected: layer.showFromStart,
        };
      });

      const groupName =
        layersInGroup.length > 1
          ? layerGroup[0].groupName ||
            layerGroup[0].layerName ||
            layerGroup[0].layerId
          : layerGroup[0].layerName || layerGroup[0].layerId;

      const groupId =
        layersInGroup.length > 1
          ? layerGroup[0].groupName || layerGroup[0].layerId
          : layerGroup[0].layerId;

      const groupSelected = !layersInGroup.some((layer) => !layer.selected);

      const gd: FilterGroupData = {
        id: groupId,
        title: groupName,
        groupSelected: groupSelected,
        filters: layersInGroup.length > 1 ? layersInGroup : [],
      };

      return gd;
    });

    if (currentCmsMapData?.mapType === MapTypes.LINER_SERVICE_MAP) {
      const roroFilter = {
        id: "roro",
        title: t("linermap.roroVehicle"),
        groupSelected: true,
        filters: [],
      };
      const containerFilter = {
        id: "container",
        title: t("linermap.container"),
        groupSelected: true,
        filters: [],
      };
      const bulkFilter = {
        id: "bulk",
        title: t("linermap.bulk"),
        groupSelected: true,
        filters: [],
      };

      groupData.push(roroFilter);
      groupData.push(containerFilter);
      groupData.push(bulkFilter);
    }

    return groupData;
  }, [layers]);

  // ------------------------------------
  // UPDATE FILTER SIZE
  // ------------------------------------

  const updateFilterSize = (height?: number) => {
    if (ref?.current && !height) {
      const filterElement: HTMLElement = ref.current;
      const filterBackground = filterElement.querySelector(
        ".map-modal-filter-bg-content"
      );
      if (filterBackground) {
        setFilterModalSize({
          height:
            filterElement.clientHeight < 50 ? 50 : filterElement.clientHeight,
        });
      }
    } else {
      setFilterModalSize({ height: height });
    }
  };

  // ------------------------------------
  // OPEN FILTER MODAL
  // ------------------------------------

  const openFilterModal = () => {
    setShowFilterModal(true);
    filterModalOpen();
    updateFilterSize();
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
    updateFilterSize(50);
  };

  return (
    <FilterContext.Provider
      value={{
        activeFilterGroup,
        setActiveFilterGroup,
        activeFilters,
        setActiveFilters,
      }}
    >
      <div
        id="map-modal-filter"
        ref={ref}
        className={`map-modal-filter ${showFilterButton ? "show-button" : ""} ${
          showFilterModal ? "show-modal" : ""
        }`}
      >
        <button
          title={`${t("map.openFilter")}`}
          type="button"
          className="map-filter-button"
          onClick={showFilterModal ? closeFilterModal : openFilterModal}
        >
          <i className="icon bi bi-filter"></i>
        </button>
        <div className="map-filter-content">
          <MapFilterSearch
            map={map}
            searchData={searchData}
            customMapLayers={customMapLayers}
            isLinerMap={
              currentCmsMapData?.mapType === MapTypes.LINER_SERVICE_MAP
            }
          />
          <div className="map-filters">
            {groupFilterData.map((filter: FilterGroupData) => (
              <MapFilterDropdown
                key={filter.id}
                id={filter.id}
                filterGroupData={filter}
              />
            ))}
          </div>
        </div>

        <button
          title={`${t("map.closeFilter")}`}
          type="button"
          className="map-filter-close-button"
          onClick={closeFilterModal}
        >
          <i className="map-filter-close-icon bi bi-x"></i>
        </button>
        <div className="map-modal-filter-bg">
          <div className="map-modal-filter-bg-content"></div>
        </div>
      </div>
    </FilterContext.Provider>
  );
};

export default MapFilter;
